import React from 'react';

import { DocumentNode } from 'graphql';

import { apolloHooks } from '@nxte-nl/apollo-hooks';
import { callbackHooks } from '@nxte-nl/callback-hooks';

export type BodyResponse<T,> = {
  data: { body: T };
};

export type BaseResponse<T,> = {
  data: T;
};

export type UseCacheQueryParams<T,> = {
  handleResponse: (...args: (BodyResponse<T>
    | BaseResponse<T> | null)[]) => void;
  query: DocumentNode;
};

export const cacheHooks = {
  useCachedData<T,>(
    query: DocumentNode,
    setData: (data: T | null) => void,
    setLoading: (loading: boolean) => void,
    isResponseBody?: boolean,
  ) {
    const [data, setDataState] = React.useState<T | null>(null);
    const setDataHandler = isResponseBody
      ? callbackHooks.useResponseBody(setData)
      : callbackHooks.useResponseData(setData);

    const setDataStateHandler = isResponseBody
      ? callbackHooks.useResponseBody<T>(setDataState)
      : callbackHooks.useResponseData<T>(setDataState);

    const handleResponse = callbackHooks.useWrapCallbacks<
      BodyResponse<T> | BaseResponse<T> | null>([
        setDataHandler,
        setDataStateHandler,
      ]);

    const {
      executeRequest,
      loading,
    } = cacheHooks.useCacheQuery<T>({
      handleResponse,
      query,
    });

    React.useEffect(() => {
      if (data === null) {
        executeRequest({});
      }
    }, [data, executeRequest]);

    React.useEffect(() => {
      setLoading(loading);
    }, [loading, setLoading]);
  },
  useCacheQuery<T,>({
    handleResponse,
    query,
  }: UseCacheQueryParams<T>) {
    const [executeRequest, { loading } ] = apolloHooks.useLazyQuery<BaseResponse<T>>(
      query,
      {
        onCompleted: handleResponse,
      },
    );
    return {
      executeRequest,
      loading,
    };
  },
};
